import { ReactNode, useEffect, useRef } from 'react';

import { ErrorBoundary } from '@/boundaries/ErrorBoundary';
import { isPostHogEnabled, postHogHost, postHogKey } from '@/helpers/posthog';
import useAccessToken from '@/hooks/useAccessToken';
import useAppSelector from '@/hooks/useAppSelector';
import { PostHogProvider } from '@posthog/react';
import posthog from 'posthog-js';

interface IPostHogContextProps {
  children?: ReactNode;
}

const buildDistinctId = (
  idCliente: string,
  agentId: number | null,
  idUsuario: string,
) => {
  if (!idCliente) return null;

  if (agentId) return `agent:${idCliente}:${agentId}`;

  if (idUsuario) return `user:${idCliente}:${idUsuario}`;

  return null;
};

export const PostHogContext = ({ children }: IPostHogContextProps) => {
  const prevAgentIdRef = useRef<string | null>(null);
  const agent = useAppSelector(
    (state) =>
      state.payloadAgente?.UserPayloadAgent?.agentePayload?.agentePayload,
  );

  const { idCliente, idUsuario } = useAccessToken();

  useEffect(() => {
    if (!isPostHogEnabled()) {
      return;
    }

    posthog.init(postHogKey() as string, {
      api_host: postHogHost() || 'https://us.i.posthog.com',
      person_profiles: 'identified_only',
      capture_exceptions: true,
      capture_performance: { network_timing: true },
      defaults: '2025-11-30',
      session_recording: {
        strictMinimumDuration: true,
        recordHeaders: true,
        recordBody: true,
        maskCapturedNetworkRequestFn: (request) => request,
      },
      loaded: (posthog) => {
        if (process.env.NODE_ENV === 'development') posthog.debug();
      },
    });
  }, []);

  useEffect(() => {
    if (!isPostHogEnabled()) return;

    const currentId = buildDistinctId(
      idCliente,
      agent?.agente?.id ?? null,
      idUsuario,
    );

    if (prevAgentIdRef.current && prevAgentIdRef.current !== currentId) {
      posthog.reset();
    }

    if (currentId) {
      posthog.identify(currentId, {
        name: agent?.agente?.nome ?? `User with id ${idUsuario}`,
        login: agent?.agente?.login,
        clientId: idCliente,
      });
    }

    prevAgentIdRef.current = currentId;
  }, [
    agent?.agente?.id,
    agent?.agente?.login,
    agent?.agente?.nome,
    idCliente,
    idUsuario,
  ]);

  if (!isPostHogEnabled()) return <>{children}</>;

  return (
    <PostHogProvider client={posthog}>
      <ErrorBoundary>{children}</ErrorBoundary>
    </PostHogProvider>
  );
};
